import { useEffect, useRef } from "react";
import { useAuth as useClerkAuth } from "@clerk/react";
import { useAuth } from "@/hooks/use-auth";
import { exchangeClerkToken } from "@/lib/auth-client";
import { CLERK_PENDING_KEY, registerClerkSignOut } from "@/lib/clerk";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/i18n";

/**
 * Invisible bridge between Clerk (Google OAuth front-door) and Kitdex's own
 * session system. When the user returns from the Google redirect with a
 * fresh Clerk session AND the pending flag is set, it exchanges the Clerk
 * token for a kd_session via the API, then completes local sign-in.
 *
 * The pending flag (set right before the redirect) is what prevents a stale
 * Clerk browser session from silently re-signing-in a user who signed out.
 */
export function GoogleAuthBridge() {
  const { isSignedIn, getToken, signOut: clerkSignOut } = useClerkAuth();
  const { user, completeExternalSignIn } = useAuth();
  const { toast } = useToast();
  const t = useT();
  const busyRef = useRef(false);

  // Let the local auth provider trigger Clerk sign-out (best-effort).
  useEffect(() => {
    registerClerkSignOut(() => clerkSignOut());
    return () => registerClerkSignOut(null);
  }, [clerkSignOut]);

  useEffect(() => {
    if (!isSignedIn || user || busyRef.current) return;
    if (!localStorage.getItem(CLERK_PENDING_KEY)) return;
    busyRef.current = true;
    (async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error("no clerk token");
        const u = await exchangeClerkToken(token);
        localStorage.removeItem(CLERK_PENDING_KEY);
        completeExternalSignIn(u);
      } catch (err) {
        localStorage.removeItem(CLERK_PENDING_KEY);
        toast({
          variant: "destructive",
          title: t("auth.googleFailedTitle"),
          description: humanizeExchangeError(err, t("auth.googleFailedBody")),
        });
        // Drop the orphaned Clerk session so a retry starts clean.
        clerkSignOut().catch(() => undefined);
      } finally {
        busyRef.current = false;
      }
    })();
  }, [isSignedIn, user, getToken, completeExternalSignIn, clerkSignOut, toast, t]);

  return null;
}

function humanizeExchangeError(err: unknown, fallback: string): string {
  if (typeof err === "object" && err && "data" in err) {
    const data = (err as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  return fallback;
}
